import { useEffect } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { motion, useCycle } from 'framer-motion';
import theme from 'config/theme';
import { ENV_CONFIG } from 'config/environment';
import { Row } from 'components/Row';
import Button from 'components/Button';
import Banner from 'components/Banner';
import Icon, { IconNames } from 'components/Icon';
import { ActionListItem } from 'components/ActionListItem';
import { AccountMenu } from './AccountMenu';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import { getPathWithRef, getUrlParams, noop } from 'helpers/utils';
import { getHeaderBarConfig } from 'selectors/siteConfig';
import { getSiteAccessToken, getUserName, setRefValue } from 'services/storage';
import { getSiteConfig } from 'store/siteConfig/actions';
import {
  BarContainer,
  HeaderName,
  MotionButton,
  MotionDiv,
  RowHeader,
  StyledImage,
  RowLoginLargeOnly,
  RowLoginSmallOnly,
  InfoText,
  HeaderItemsContainer,
} from './styled';
import { useRedirectAfterLogin } from 'hooks/useRedirectAfterLogin';

export const Bar = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { urlRef } = getUrlParams();
  const { handleUpdateCredentials } = useRedirectAfterLogin();
  const [isOpen, toggleOpen] = useCycle(false, true);
  const { pathname } = useLocation();
  const isMainScreen = pathname === '/';
  // startsWith, not ===: router.tsx's /profile layout route also covers /profile/
  // and /profile/edit, and the account menu should persist across all of it.
  const isProfileScreen = pathname.startsWith('/profile');
  // Only header route hosting SearchScreen's leave-cart guard - update if SearchScreen moves.
  const isCartScreen = useMatch('/cart') !== null;

  const { bannerConfig, headerConfig } = useAppSelector(getHeaderBarConfig);

  useEffect(() => {
    dispatch(getSiteConfig()).catch(noop);
  }, []);

  const sidebar = {
    open: () => ({
      clipPath: 'inset(0 0 0% 0%)',
      transition: {
        delay: 0.1,
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    }),
    closed: {
      clipPath: 'inset(0 100% 0% 0%)',
      transition: {
        delay: 0.1,
        type: 'spring',
        stiffness: 400,
        damping: 40,
      },
    },
  };

  const loginClick = () => {
    if (urlRef) setRefValue(urlRef);
    const url = new URL(window.location.href);
    const pathname = url.pathname === '/' ? '/profile' : url.pathname;
    handleUpdateCredentials(`${pathname}${url.search}`);
  };

  const handleJoinOathClick = () => {
    const targetElement = document.getElementById('footer-signup');
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getLoggedInDisplayText = () => {
    const userName = getUserName();
    return userName && userName !== 'null' && userName !== 'undefined'
      ? `Hi, ${userName}`
      : 'My Account';
  };

  const handleLinkPressed = (path: string) => {
    trackEvent('Header Nav Clicked', {
      destination: path,
    });
    navigate(getPathWithRef(path));
    // The reload resets same-route screens that read the URL only on mount (e.g. Set). On /cart
    // it would drop the cart without the leave-cart prompt, and SearchScreen follows the URL anyway.
    if (!isCartScreen) navigate(0);
  };

  return (
    <>
      {bannerConfig?.banner_config === 'on' && (
        <Banner
          text={bannerConfig?.banner_text}
          link={bannerConfig?.banner_link}
          background={bannerConfig?.banner_background}
          textColor={bannerConfig?.banner_text_color}
        />
      )}
      <BarContainer $isOpen={isOpen}>
        <RowHeader $justifyContent="space-between">
          <div role="header" className="align-center">
            <Row $alignItems="center">
              <MotionButton whileTap={{ scale: 0.9 }} $isFromSidebar>
                <Icon
                  onClick={toggleOpen}
                  name={isOpen ? IconNames.XClose : IconNames.Menu}
                  size={32}
                  color={theme.colors.inkBlue}
                  style={{ padding: 16 }}
                />
              </MotionButton>
              <StyledImage
                alt="Oath logo"
                onClick={() => navigate(getPathWithRef('/'))}
                src={`${
                  ENV_CONFIG().CLOUD_STORAGE.URL
                }/static_content/oath_logos/Oath_FullLockup_Color_DarkText.svg`}
              />
              <HeaderItemsContainer>
                {headerConfig?.spots?.map(spot => (
                  <InfoText
                    key={spot.header_title}
                    onClick={() => handleLinkPressed(spot.header_link)}
                  >
                    {spot.header_title}
                  </InfoText>
                ))}
              </HeaderItemsContainer>
            </Row>
          </div>
          <Row>
            {getSiteAccessToken() ? (
              isProfileScreen ? (
                <AccountMenu />
              ) : (
                <Row>
                  <Icon
                    name={IconNames.User}
                    size={32}
                    color={theme.colors.black}
                    style={{ padding: '16px 5px' }}
                  />
                  <HeaderName $isCentered>
                    <Link
                      to={getPathWithRef('/profile')}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                      data-dd-privacy="mask"
                      data-dd-action-name="My Account"
                    >
                      {getLoggedInDisplayText()}
                    </Link>
                  </HeaderName>
                </Row>
              )
            ) : (
              <>
                <RowLoginLargeOnly>
                  <Row $pressable onClick={loginClick}>
                    <Icon
                      name={IconNames.User}
                      size={32}
                      color={theme.colors.black}
                      style={{ padding: '16px 5px' }}
                    />
                    <HeaderName $isCentered>Log In</HeaderName>
                  </Row>
                  <Button
                    text="JOIN OATH"
                    onPress={handleJoinOathClick}
                    backgroundColor={theme.colors.oathBlue}
                  />
                </RowLoginLargeOnly>
                <RowLoginSmallOnly>
                  <Button
                    text="LOG IN"
                    onPress={loginClick}
                    backgroundColor={theme.colors.oathBlue}
                  />
                </RowLoginSmallOnly>
              </>
            )}
          </Row>
        </RowHeader>
        <div className="footer-divider" />
        <motion.nav initial={false} animate={isOpen ? 'open' : 'closed'}>
          <MotionDiv variants={sidebar}>
            <ActionListItem
              onclick={() => navigate(getPathWithRef('/'))}
              label="Home"
              selected={isMainScreen}
            />
            <ActionListItem
              onclick={() => navigate(getPathWithRef('/donor'))}
              label="About Oath"
              selected={window.location.href.includes('donor')}
            />
            <ActionListItem
              onclick={() => navigate(getPathWithRef('/cart'))}
              label="Search Candidates"
              selected={window.location.href.includes('cart')}
            />
            <ActionListItem
              onclick={() => navigate(getPathWithRef('/set?p=top-issues'))}
              label="Top Issues"
              selected={window.location.href.includes('set?p=top-issues')}
            />
            <ActionListItem
              onclick={() => navigate(getPathWithRef('/autopilot'))}
              label="Donate Monthly"
              selected={window.location.href.includes('autopilot')}
            />
            <ActionListItem
              onclick={() => navigate(getPathWithRef('/impact_score'))}
              label="Our Algorithm"
              selected={window.location.href.includes('impact_score')}
            />
            <ActionListItem
              onclick={() => navigate(getPathWithRef('/contact'))}
              label="Contact us"
              lastone
              selected={window.location.href.includes('contact')}
            />
          </MotionDiv>
        </motion.nav>
      </BarContainer>
    </>
  );
};
